/** Stored approval headers are history records, never occurrence-shaped review rows. */
import { ActivityEntityName, EntityViewDisplay, JobSecurityRight, SurfaceType, VisitApprovalCommandId, VisitApprovalGetRequest, VisitApprovalSummary } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { EntityDataService } from '@workbuddy/services/entities/entity.data.service';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { ApprovalPeriodComponent } from './field.renderers/approval.period/approval.period.component';
import { formatApprovalPeriod } from './visit.approval.presentation';
import { ValueFormatterParams } from 'ag-grid-enterprise';
import { readVisitApprovalScope, toVisitApprovalDetailModel, VisitApprovalDetailModel } from './visit.approval.detail.model';

export const visitApprovalManifest = registerManifest<VisitApprovalDetailModel>({
  entity: ActivityEntityName.VisitApproval,
  board: {
    supportedDisplays: [EntityViewDisplay.Table],
    panel: { kind: 'quickview', ownsHeader: false },
    defaults: {
      readonly: true,
      paging: 'hasMore',
      header: { title: 'Approval history', hideAdd: true, showPaginator: true },
      table: {
        overrideCellRenderer: (_context, _metadata, field, renderer) =>
          field.name === 'name' || field.name === 'reviewContext.label'
            ? {
                ...renderer,
                cellRenderer: ApprovalPeriodComponent,
                valueFormatter: (params: ValueFormatterParams<VisitApprovalDetailModel>) => formatApprovalPeriod(params.data),
              }
            : renderer,
        columns: {
          byField: {
            name: {
              headerName: 'Review period',
              minWidth: 220,
            },
          },
        },
      },
    },
  },
  surfaces: {
    [SurfaceType.Manage]: () =>
      import('./surface/manage/visit.approval.manage.surface').then((module) => module.visitApprovalManageSurface),
  },
  operations: {
    create: {
      kind: 'custom',
      load: () =>
        import('./board/visit.approval.board/visit.approval.board.component').then(
          (module) => module.VisitApprovalBoardComponent,
        ),
      presentation: { mode: 'pseudo-page', title: 'New visit approval' },
      authorize: ({ injector }) => injector.get(RightsService).hasRights(JobSecurityRight.Manage),
    },
    edit: {
      kind: 'custom',
      load: () =>
        import('./board/visit.approval.board/visit.approval.board.component').then(
          (module) => module.VisitApprovalBoardComponent,
        ),
      presentation: { mode: 'pseudo-page', title: 'Review visit approval' },
      authorize: ({ injector }) => injector.get(RightsService).hasRights(JobSecurityRight.Manage),
    },
    preview: {
      surface: SurfaceType.Manage,
      authorize: ({ injector }) => injector.get(RightsService).hasRights(JobSecurityRight.CanAccess),
      loadRecord: async ({ id, params, injector }) => {
        const scope = readVisitApprovalScope(params);
        if (!scope) throw new Error('Open this approval from a customer, site, or service plan to establish its viewing scope.');
        const request: VisitApprovalGetRequest = { approvalId: id, scope };
        const summary = await injector.get(EntityDataService).executeCommand<VisitApprovalSummary>(ActivityEntityName.VisitApproval, VisitApprovalCommandId.Get, request);
        return toVisitApprovalDetailModel(summary, formatApprovalPeriod(summary));
      },
    },
  },
});
